import { useState, useEffect, type ReactNode } from "react";
import RollingLineGraph from "./RollingLineGraph";
import type { TelemetryValue } from "../types/telemetry";
import type { GaugeConfigEntry } from "../types/config";

interface GaugePanelProps {
  metrics: Map<string, TelemetryValue>;
  gauges: GaugeConfigEntry[];
  topicPrefix: string;
  chips?: ReactNode;
}

export function GaugePanel({ metrics, gauges, topicPrefix, chips }: GaugePanelProps) {
  const [seenGauges, setSeenGauges] = useState<Set<string>>(() => new Set());

  useEffect(() => {
    setSeenGauges(prev => {
      let changed = false;
      const next = new Set(prev);
      gauges.forEach(cfg => {
        const key = cfg.topic.replace(topicPrefix, "");
        const val = metrics.get(key)?.value;
        if (!next.has(cfg.id) && typeof val === "number" && !isNaN(val)) {
          next.add(cfg.id);
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  }, [metrics, gauges, topicPrefix]);

  const seenList = gauges.filter(cfg => seenGauges.has(cfg.id));
  const unseenList = gauges.filter(cfg => !seenGauges.has(cfg.id));

  return (
    <div className="gauge-panel">
      {seenList.map((cfg) => {
        const key = cfg.topic.replace(topicPrefix, "");
        const telemetry = metrics.get(key);

        return (
          <div key={cfg.id} className="gauge-cell">
            <RollingLineGraph
              config={cfg}
              telemetry={telemetry}
            />
          </div>
        );
      })}
      
      {(chips || unseenList.length > 0) && (
        <div className="gauge-panel__chips">
          {chips}
          {unseenList.map(cfg => (
            <div key={cfg.id} className="gauge-chip">
              <span className="gauge-chip__label">{cfg.label}</span>
              <span className="gauge-chip__value">--</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
